import { useMemo, ChangeEvent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { InvestorSpaceModel, SortParams, SortParamsArg, Tab } from 'models';
import { investorSpaceAddonTableCells } from 'configs';

import {
  Table,
  TablePagination,
  Tabs,
  Loader,
  Error,
  Centered,
} from 'components';

import { useStyles } from './style';

interface Props {
  investorSpaces: InvestorSpaceModel[];
  sortParams: SortParams;
  activeTab: string;
  pageNumber: number;
  pageSize: number;
  totalCount: number;
  refresh: boolean;
  loading: boolean;
  error: boolean;
  handleChangeTab: (id: string) => void;
  handleChangeSortParams: (params: SortParamsArg) => void;
  handleChangeCurrentPage: (e: ChangeEvent<unknown>, page: number) => void;
}

const SpacesAddonDetails: React.FC<Props> = ({
  investorSpaces,
  sortParams,
  activeTab,
  pageSize,
  pageNumber,
  totalCount,
  refresh,
  loading,
  error,
  handleChangeTab,
  handleChangeSortParams,
  handleChangeCurrentPage,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const tabsClasses = useMemo(
    () => ({
      ul: classes.tabsRoot,
      tab: classes.tab,
      tabRoot: classes.tabRoot,
    }),
    [classes]
  );

  const tabs: Tab[] = useMemo(() => {
    return [
      {
        id: '100000000',
        label: t('#portal.investor.dashboard.spaces.vacant'),
        warning: false,
      },
      {
        id: '100000003',
        label: t('#portal.investor.dashboard.spaces.releasing'),
        warning: false,
      },
    ];
  }, [t]);

  const renderBody = () => {
    return (
      <div>
        <h4 className={classes.title}>
          {t('#portal.investor.dashboard.spaces')}
        </h4>
        <Tabs
          activeTab={activeTab}
          tabs={tabs}
          onSelect={handleChangeTab}
          cls={tabsClasses}
        />
        {refresh ? (
          <Centered>
            <Loader size={30} themeColor="darkBlue" />
          </Centered>
        ) : (
          <Table
            list={investorSpaces}
            order={sortParams.order}
            orderBy={sortParams.orderBy}
            tableCells={investorSpaceAddonTableCells}
            handleChangeSortParams={handleChangeSortParams}
          />
        )}
        <div className={classes.mt20}>
          <TablePagination
            currentPage={pageNumber}
            totalPages={Math.ceil(totalCount / pageSize)}
            onChange={handleChangeCurrentPage}
          />
        </div>
      </div>
    );
  };

  return (
    <>
      {error ? (
        <Centered>
          <Error />
        </Centered>
      ) : null}
      {loading ? (
        <Centered>
          <Loader size={30} themeColor="darkBlue" />
        </Centered>
      ) : null}
      {loading || error ? null : renderBody()}
    </>
  );
};

export default memo(SpacesAddonDetails);
